import {Injectable, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@q9elements/ui-kit/common';
import {BehaviorSubject, EMPTY, Observable, Subject, defer, from, merge} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  shareReplay,
  switchMap
} from 'rxjs/operators';
import {UndoService} from 'src/app/commands/undo.service';
import {AuthService} from 'src/app/core/auth/auth.service';
import {NavigationService} from 'src/app/core/navigation/navigation.service';
import {RequestsService} from 'src/app/core/requests/requests.service';
import {LocalStorageService} from 'src/app/core/services/local-storage.service';
import {PopupManagerService} from 'src/app/core/services/popup-manager.service';
import {VersionsService} from 'src/app/core/services/versions.service';
import {DiagramJsonHandlerService} from 'src/app/diagram-data/diagram-json-handler.service';
import {DiagramUpdateService} from 'src/app/diagram-data/diagram-update.service';
import {isComponent, isConnector} from 'src/app/diagram-render/helpers/shapes-detectors.util';
import {EditorRegistryService} from 'src/app/diagram-render/mx-graph/editor/editor-registry.service';
import {DialogType} from 'src/app/editor-interface/dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import {GraphModeService} from 'src/app/editor-state/graph-mode.service';
import {Link} from 'src/app/shared/models/connector-link.interface';
import {Connector} from 'src/app/shared/models/connector.interface';
import {CopyPasteConnectorData} from 'src/app/shared/models/copy-paste-connector-data.interface';
import {DiagramVersionJSON} from 'src/app/shared/models/diagram-version.interface';
import {ConnectorLink} from 'src/app/shared/utils/connector-link';
import {
  CONNECTOR_ERROR_TRANSLATIONS,
  ConnectorError,
  DEFAULT_CONNECTOR_ERROR,
  DE_CONNECTOR_LINK_CACHE,
  EMPTY_BUFFER
} from 'src/app/shared/utils/constants';
import {CopyPasteBuffer} from 'src/app/shared/utils/copy-paste-buffer';

@Injectable({providedIn: 'root'})
export class FlowlineAssociationService {
  private linksUrl$ = new BehaviorSubject<string>('');
  private update$ = new Subject<string>();
  public loadingLinks$ = new BehaviorSubject<boolean>(false);
  public links$ = merge(
    this.update$,
    this.linksUrl$.pipe(
      filter(url => url !== ''),
      distinctUntilChanged()
    )
  ).pipe(
    switchMap(url =>
      defer(() => {
        this.loadingLinks$.next(true);

        return (
          from(
            this.requestService.addToQueue(
              {
                isApi: true,
                url,
                method: 'GET'
              },
              true
            )
          ) as Observable<Link[]>
        ).pipe(
          catchError(() => EMPTY),
          finalize(() => this.loadingLinks$.next(false))
        );
      })
    ),
    shareReplay(1)
  );
  private _refresh$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private diagramJsonHandlerService: DiagramJsonHandlerService,
    private diagramUpdateService: DiagramUpdateService,
    private editorRegistryService: EditorRegistryService,
    private graphModeService: GraphModeService,
    private navigationService: NavigationService,
    private popupManagerService: PopupManagerService,
    private requestService: RequestsService,
    private snackBarService: SnackBarService,
    private translateService: TranslateService,
    private undoService: UndoService,
    private versionsService: VersionsService,
    private ngZone: NgZone
  ) {}

  public initLinks(mxKey: string): void {
    const currentNav = this.navigationService.getCurrent();
    const url = `/diagram/${currentNav.id}/state/${currentNav.state}/flowline/${mxKey}/connection`;

    this.linksUrl$.next(url);
  }

  public navigate(link: Link): void {
    const {map} = this.diagramJsonHandlerService.get();
    const isGraphModeEnabled = this.graphModeService.get();
    const isSameMap = String(link.mapId) === String(map.id);
    const isEdit = isGraphModeEnabled && isSameMap;

    const navigate$ = from(this.navigationService.relative(link.diagramId, isEdit));

    navigate$
      .pipe(
        switchMap(() => this.diagramJsonHandlerService.diagramJson$),
        first(json => json.diagramId === link.diagramId),
        finalize(() => {
          const flowline = this.editorRegistryService.getGraph().model.getCell(link.flowlineMxKey);

          this.highlight(isConnector(flowline.source) ? flowline.source : flowline.target);
          this.versionsService.getMapAuthorisationMode();
        })
      )
      .subscribe();
  }

  public copy(cell: any): void {
    const {id, email, role, state, userInTeam} = this.authService.getUserDataSync();
    const user = {id, email, role, state};
    const diagramData: DiagramVersionJSON = this.diagramJsonHandlerService.get();
    const link = new ConnectorLink(diagramData);
    const buffer = new CopyPasteBuffer({user});

    const [edge] = cell.edges;

    if (!edge) {
      return;
    }

    link.connector.id = String(cell.id);
    link.flowline.id = String(edge.id);

    buffer.user.organization = userInTeam.organization?.id;
    buffer.link = link;

    LocalStorageService.set(DE_CONNECTOR_LINK_CACHE, buffer.toString());

    const message = this.translateService.instant('CONNECTOR.MESSAGE_COPIED');

    this.showSnackBar(message);
  }

  public canPaste(cell: any): boolean {
    const bufferData =
      LocalStorageService.get<CopyPasteConnectorData>(DE_CONNECTOR_LINK_CACHE) ?? EMPTY_BUFFER;
    const buffer = new CopyPasteBuffer(bufferData);

    if (!buffer.hasLink()) {
      return false;
    }

    const {team, diagramId} = this.diagramJsonHandlerService.get();

    const [edge] = cell.edges;

    if (!edge) {
      return false;
    }

    const isUserInSpace = buffer.link.diagramVersion?.spaceId === team?.id;
    const isSameDiagram = String(buffer.link.diagramVersion?.diagramId) === diagramId;

    return isUserInSpace && !isSameDiagram;
  }

  public connect(cell: any): void {
    const bufferData =
      LocalStorageService.get<CopyPasteConnectorData>(DE_CONNECTOR_LINK_CACHE) ?? EMPTY_BUFFER;
    const buffer = new CopyPasteBuffer(bufferData);

    if (!buffer.hasLink()) {
      return;
    }

    const {id: diagramVersionDbId} = this.diagramJsonHandlerService.get();
    const [edge] = cell?.edges ?? [];

    if (!edge) {
      return;
    }

    const source: Connector = {
      mxKey: Number(buffer.link?.flowline?.id),
      diagramVersionDbId: buffer.link?.diagramVersion?.id
    };

    const target: Connector = {
      mxKey: Number(edge.id),
      diagramVersionDbId
    };

    this.connectRequest(target, source)
      .pipe(
        catchError(error => this.handleError(error)),
        finalize(() => {
          const message = this.translateService.instant('CONNECTOR.MESSAGE_CREATED');

          this.showSnackBar(message);
          this._refresh$.next(true);
          const url = this.linksUrl$.value;

          this.update$.next(url);
        })
      )
      .subscribe();
  }

  private showSnackBar(message: string) {
    this.ngZone.run(() => {
      this.snackBarService.success(message);
    });
  }

  public disconnect(target: string): void {
    this.popupManagerService
      .openConfirmDeleteDialog([DialogType.DEL_FL_CONN])
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.undoService.clear();
          this.diagramUpdateService.update({deleteFlowlineConnection: {target}}, true);
          this._refresh$.next(true);
          const url = this.linksUrl$.value;

          this.update$.next(url);
        }
      });
  }

  private highlight(cell: any): void {
    this.editorRegistryService.getGraph().stopEditing();

    if (isComponent(cell)) {
      if (cell.comp.instance.highlight) {
        cell.comp.instance.highlight();
      }
    }
  }

  private handleError(errorStatus: ConnectorError): Observable<never> {
    const message = CONNECTOR_ERROR_TRANSLATIONS[errorStatus] ?? DEFAULT_CONNECTOR_ERROR;

    this.popupManagerService.openErrorDialog({message});

    return EMPTY;
  }

  private connectRequest(source: Connector, target: Connector): Observable<unknown> {
    return from(
      this.diagramUpdateService.update(
        {
          createFlowlineConnection: {
            source: source,
            target: target
          }
        },
        true
      )
    );
  }

  public get refresh$(): Observable<boolean> {
    return this._refresh$;
  }
}
